"use client";

import { useEffect, useState } from "react";
import * as d3 from "d3";
import kongImage from "/public/images/nos-regions/kong.jpg";
import basiliqueImage from "/public/images/nos-regions/basilique.jpg";
import berebyImage from "/public/images/nos-regions/bereby.jpg";
import taiImage from "/public/images/nos-regions/tai.jpg";
import bassamImage from "/public/images/nos-regions/bassam.jpg";
import { NavigateFunction } from "react-router-dom";

interface Marker {
  id: string;
  x: number;
  y: number;
  content: React.ReactNode;
  onClick?: () => any;
}

interface CiInteractiveMapProps {
  strokeColor?: string;
  markers?: Marker[];
}

export default function CiInteractiveMap({
  strokeColor = "#F1F5F9",
  markers = [],
}: Readonly<CiInteractiveMapProps>) {
  const [activeMarker, setActiveMarker] = useState<Marker | null>(null);

  useEffect(() => {
    const svg = d3.select("#ci-interactive-map");

    // Regions interaction logic
    svg
      .select(".regions")
      .selectAll("path")
      .on("mouseover", function (event) {
        if (d3.select(this).attr("class") !== "not-take") {
          d3.select(this).attr("fill", "#ff9b02");
          d3.select(this).attr("cursor", "pointer");

          const region = d3.select(this).attr("data-region");
          const [x, y] = d3.pointer(event);

          d3.select("#region-tooltip")
            .style("left", `${x}px`)
            .style("top", `${y}px`)
            .style("display", "block")
            .text(region);
        }
      })
      .on("mouseout", function () {
        d3.select(this).attr("fill", "#E5E7EB");
        d3.select("#region-tooltip").style("display", "none");
      });

    // Set stroke color
    svg.select(".stroke").selectAll("path").attr("stroke", strokeColor);

    // Region click handler
    svg.selectAll(".regions path").on("click", function () {
      d3.select(this).attr("fill", "green");
    });

    // Add markers
    const markerGroup = svg.select(".markers");

    // Clear existing markers first
    markerGroup.selectAll("*").remove();

    // Add new markers
    markers.forEach((marker) => {
      markerGroup
        .append("circle")
        .attr("cx", marker.x)
        .attr("cy", marker.y)
        .attr("r", 12)
        .attr("fill", "#EF4444")
        .attr("stroke", "#FFFFFF")
        .attr("stroke-width", 1.5)
        .attr("data-id", marker.id)
        .attr("cursor", "pointer")
        .on("mouseover", function (event) {
          d3.select(this).attr("r", 16).attr("fill", "#DC2626");

          setActiveMarker(marker);
        })
        .on("mouseout", function () {
          d3.select(this).attr("r", 12).attr("fill", "#EF4444");

          setActiveMarker(null);
        })
        .on("click", function () {
          if (marker.onClick) {
            marker.onClick();
          }
        });
    });
  }, [strokeColor, markers]);

  return (
    <div className="relative">
      <svg
        id="ci-interactive-map"
        width="333"
        height="347"
        viewBox="0 0 333 347"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g className={"regions"}>
          <path
            d="M42.5141 251.435L63.3734 262.883L67.0485 275.558L81.3696 271.469V247.346L75.6435 242.848L79.7336 229.764L87.7092 225.062L83.6191 209.321V180.7L87.7092 169.865L82.1876 165.776L83.6191 156.576L79.7336 154.941L83.6191 148.603H73.803L63.1689 139.812L58.6698 143.697V153.918L49.6717 156.576L41.9006 150.239L37.197 147.172H29.0169L22.0638 172.931L11.6341 168.433L8.3621 172.931L18.7917 191.944L16.7467 213.409L1 227.72L11.6341 237.329H33.925L42.5141 251.435Z"
            fill="#E5E7EB"
            data-region="Region 1"
          />
          <path
            d="M31.6754 136.337L37.197 147.172L41.9006 150.239L49.6717 156.576L58.6698 153.918V143.697L63.1689 139.812L73.803 148.603H83.6191L79.7336 154.941L83.6191 156.576L89.3452 159.438L107.137 158.416L115.113 162.3L126.36 159.438L134.131 167.002L141.289 161.074H149.878L155.809 172.931L157.036 163.527L152.946 158.416L154.377 146.354H163.375L166.034 140.63L171.555 141.857L174.623 134.293L159.899 124.275V119.778L141.289 113.031L128.201 82.3657L100.593 87.2722L76.8705 94.8363V119.778L48.4447 90.3387L37.8105 92.9964L37.197 103.832L51.9212 113.031V127.342L37.197 121.822H20.8368V134.701L31.6754 136.337Z"
            fill="#E5E7EB"
            data-region="Region 2"
          />
          <path
            d="M37.8105 82.3657L48.4447 90.3387L76.8705 119.778V94.8363L100.593 87.2722V48.2248L88.3227 42.705L95.0713 34.1187L108.159 31.2566L105.501 20.8303H90.7767L86.0732 31.2566L53.5572 14.4928L26.3583 36.7764V61.7176L30.2439 71.5306H42.5141L37.8105 82.3657Z"
            fill="#E5E7EB"
            data-region="Region 3"
          />
          <path
            d="M105.501 3.65767V20.8303L108.159 31.2566L95.0713 34.1187L88.3227 42.705L100.593 48.2248V87.2722L128.201 82.3657L141.289 113.031L159.899 119.778L154.377 103.832L167.465 100.561L175.441 73.5749L189.143 75.8237L197.323 87.2722V98.5162H218.796L226.976 108.942L249.675 113.031L257.856 103.832L249.675 92.9964L252.129 87.2722L235.565 75.8237L228.816 61.7176L218.796 54.5624L209.797 56.1978L186.28 23.6924H172.782L158.263 12.4484L141.289 29.6211L130.45 26.5546L128.201 1H118.18V9.9952L105.501 3.65767Z"
            fill="#E5E7EB"
            data-region="Region 4"
          />
          <path
            d="M154.377 103.832L159.899 119.778V124.275L174.623 134.293L171.555 141.857L166.034 140.63L163.375 146.354H154.377L152.946 158.416L157.036 163.527L155.809 172.931L158.467 187.855L170.124 184.584L178.099 188.468L197.936 172.931L199.777 169.047L211.433 172.931L219.205 167.616L218.182 156.576H227.794L231.884 151.67L236.178 154.123L241.904 151.67V147.785L245.585 144.514L249.675 113.031L226.976 108.942L218.796 98.5162H197.323V87.2722L189.143 75.8237L175.441 73.5749L167.465 100.561L154.377 103.832Z"
            fill="#E5E7EB"
            data-region="Region 5"
          />
          <path
            d="M239.45 48.2248L228.816 61.7176L235.565 75.8237L252.129 87.2722L249.675 92.9964L257.856 103.832L249.675 113.031L245.585 144.514L273.602 181.517L296.302 198.894L308.368 156.985L328 136.337L320.842 100.561L311.231 90.3387L316.957 79.708V65.3975L313.276 71.5306L300.801 56.1978L275.443 42.705L239.45 48.2248Z"
            fill="#E5E7EB"
            data-region="Region 6"
          />
          <path
            d="M273.602 181.517L245.585 144.514L241.904 147.785V151.67L236.178 154.123L231.884 151.67L227.794 156.576H218.182L219.205 167.616L211.433 172.931L199.777 169.047L197.936 172.931L178.099 188.468L170.124 184.584L158.467 187.855L167.874 199.508L175.645 196.85L187.098 199.508L195.687 211.161L194.46 214.636L191.392 212.592L179.122 220.769L177.486 224.245V235.693L182.394 234.262L191.392 242.031L231.884 241.008L233.929 245.302L239.859 241.008L238.223 230.378L257.856 222.609V203.801L273.602 181.517Z"
            fill="#E5E7EB"
            data-region="Region 7"
          />
          <path
            d="M286.077 213.409L296.302 198.894L273.602 181.517L257.856 203.801V222.609L275.238 238.964L277.488 268.403L283.009 273.105L269.308 280.874L266.445 294.98L261.332 292.935L250.698 296.955L294.462 302.339V300.091L313.276 302.339V275.354L308.368 273.105L300.801 275.354L286.077 213.409Z"
            fill="#E5E7EB"
            data-region="Region 8"
          />
          <path
            d="M261.332 292.935L249.675 276.785H225.34L222.272 279.851L230.657 282.509L234.747 296.36L241.291 295.797L250.698 296.955L261.332 292.935Z"
            fill="#E5E7EB"
            data-region="Region 9"
          />
          <path
            d="M275.238 238.964L257.856 222.609L238.223 230.378L239.859 241.008L233.929 245.302L231.884 241.008L191.392 242.031L182.394 234.262L180.144 242.848L193.437 250.412L195.687 272.287L191.392 277.603V294.98H183.621L171.555 296.36L169.51 293.957L165.216 302.339L234.747 296.36L230.657 282.509L222.272 279.851L225.34 276.785H249.675L261.332 292.935L266.445 294.98L269.308 280.874L283.009 273.105L277.488 268.403L275.238 238.964Z"
            fill="#E5E7EB"
            data-region="Region 10"
          />
          <path
            d="M182.394 234.262L177.486 235.693V224.245L171.555 222.609V220.156L152.946 228.333L138.426 226.085L129.223 236.92L118.385 238.964V247.346L127.792 270.652L134.745 268.403L140.062 280.874L151.719 279.851L153.764 274.74L173.191 287.62L169.51 293.957L171.555 296.36L183.621 294.98H191.392V277.603L195.687 272.287L193.437 250.412L180.144 242.848L182.394 234.262Z"
            fill="#E5E7EB"
            data-region="Region 11"
          />
          <path
            d="M177.486 224.245L179.122 220.769L191.392 212.592L194.46 214.636L195.687 211.161L187.098 199.508L175.645 196.85L167.874 199.508L165.216 208.503L171.555 217.703V220.156V222.609L177.486 224.245Z"
            fill="#E5E7EB"
            data-region="Region 12"
          />
          <path
            d="M165.216 208.503L167.874 199.508L158.467 187.855L155.809 172.931L149.878 161.074H141.289L134.131 167.002L126.36 159.438L115.113 162.3L107.137 158.416L89.3452 159.438L83.6191 156.576L82.1876 165.776L87.7092 169.865L83.6191 180.7V209.321L87.7092 225.062L89.3452 229.764L87.7092 232.831L93.6398 240.804L108.773 238.146L112.863 240.804L118.385 238.964L129.223 236.92L138.426 226.085L152.946 228.333L171.555 220.156V217.703L165.216 208.503Z"
            fill="#E5E7EB"
            data-region="Region 13"
          />
          <path
            d="M82.1876 330.756L165.216 302.339L169.51 293.957L173.191 287.62L153.764 274.74L151.719 279.851L140.062 280.874L134.745 268.403L127.792 270.652L118.385 247.346V238.964L112.863 240.804L108.773 238.146L93.6398 240.804L87.7092 232.831L89.3452 229.764L87.7092 225.062L79.7336 229.764L75.6435 242.848L81.3696 247.346V271.469L67.0485 275.558L67.4634 276.989L56.2158 311.539V342L82.1876 330.756Z"
            fill="#E5E7EB"
            data-region="Region 14"
          />
        </g>
        <g className={"stroke"}>
          <path
            d="M48.4447 90.3387L37.8105 82.3657L42.5141 71.5306H30.2439L26.3583 61.7176V36.7764L53.5572 14.4928L86.0732 31.2566L90.7767 20.8303H105.501M48.4447 90.3387L37.8105 92.9964L37.197 103.832L51.9212 113.031V127.342L37.197 121.822H20.8368V134.701L31.6754 136.337L37.197 147.172M48.4447 90.3387L76.8705 119.778V94.8363L100.593 87.2722M105.501 20.8303V3.65767L118.18 9.9952V1H128.201L130.45 26.5546L141.289 29.6211L158.263 12.4484L172.782 23.6924H186.28L209.797 56.1978L218.796 54.5624L228.816 61.7176M105.501 20.8303L108.159 31.2566L95.0713 34.1187L88.3227 42.705L100.593 48.2248V87.2722M228.816 61.7176L239.45 48.2248L275.443 42.705L300.801 56.1978L313.276 71.5306L316.957 65.3975V79.708L311.231 90.3387L320.842 100.561L328 136.337L308.368 156.985L296.302 198.894M228.816 61.7176L235.565 75.8237L252.129 87.2722L249.675 92.9964L257.856 103.832L249.675 113.031M296.302 198.894L286.077 213.409L300.801 275.354L308.368 273.105L313.276 275.354V302.339L294.462 300.091V302.339L250.698 296.955M296.302 198.894L273.602 181.517M165.216 302.339L82.1876 330.756L56.2158 342V311.539L67.4634 276.989L67.0485 275.558M165.216 302.339L234.747 296.36M165.216 302.339L169.51 293.957M37.197 147.172H29.0169L22.0638 172.931L11.6341 168.433L8.3621 172.931L18.7917 191.944L16.7467 213.409L1 227.72L11.6341 237.329H33.925L42.5141 251.435L63.3734 262.883L67.0485 275.558M37.197 147.172L41.9006 150.239L49.6717 156.576L58.6698 153.918V143.697L63.1689 139.812L73.803 148.603H83.6191L79.7336 154.941L83.6191 156.576M100.593 87.2722L128.201 82.3657L141.289 113.031L159.899 119.778M159.899 119.778L154.377 103.832L167.465 100.561L175.441 73.5749L189.143 75.8237L197.323 87.2722V98.5162H218.796L226.976 108.942L249.675 113.031M159.899 119.778V124.275L174.623 134.293L171.555 141.857L166.034 140.63L163.375 146.354H154.377L152.946 158.416L157.036 163.527L155.809 172.931M249.675 113.031L245.585 144.514M245.585 144.514L273.602 181.517M245.585 144.514L241.904 147.785V151.67L236.178 154.123L231.884 151.67L227.794 156.576H218.182L219.205 167.616L211.433 172.931L199.777 169.047L197.936 172.931L178.099 188.468L170.124 184.584L158.467 187.855M273.602 181.517L257.856 203.801V222.609M257.856 222.609L275.238 238.964L277.488 268.403L283.009 273.105L269.308 280.874L266.445 294.98L261.332 292.935M257.856 222.609L238.223 230.378L239.859 241.008L233.929 245.302L231.884 241.008L191.392 242.031L182.394 234.262M261.332 292.935L249.675 276.785H225.34L222.272 279.851L230.657 282.509L234.747 296.36M261.332 292.935L250.698 296.955M234.747 296.36L241.291 295.797L250.698 296.955M182.394 234.262L177.486 235.693V224.245M182.394 234.262L180.144 242.848L193.437 250.412L195.687 272.287L191.392 277.603V294.98H183.621L171.555 296.36L169.51 293.957M177.486 224.245L179.122 220.769L191.392 212.592L194.46 214.636L195.687 211.161L187.098 199.508L175.645 196.85L167.874 199.508M177.486 224.245L171.555 222.609V220.156M167.874 199.508L165.216 208.503L171.555 217.703V220.156M167.874 199.508L158.467 187.855M169.51 293.957L173.191 287.62L153.764 274.74L151.719 279.851L140.062 280.874L134.745 268.403L127.792 270.652L118.385 247.346V238.964M118.385 238.964L129.223 236.92L138.426 226.085L152.946 228.333L171.555 220.156M118.385 238.964L112.863 240.804L108.773 238.146L93.6398 240.804L87.7092 232.831L89.3452 229.764L87.7092 225.062M67.0485 275.558L81.3696 271.469V247.346L75.6435 242.848L79.7336 229.764L87.7092 225.062M87.7092 225.062L83.6191 209.321V180.7L87.7092 169.865L82.1876 165.776L83.6191 156.576M83.6191 156.576L89.3452 159.438L107.137 158.416L115.113 162.3L126.36 159.438L134.131 167.002L141.289 161.074H149.878L155.809 172.931M155.809 172.931L158.467 187.855"
            stroke={strokeColor}
          />
        </g>
        {/* Add a group for markers */}
        <g className="markers"></g>
      </svg>

      {/* Region tooltip */}
      <div
        id="region-tooltip"
        className="absolute hidden bg-gray-800 text-white px-2 py-1 rounded text-sm pointer-events-none w-[240px] z-50"
        style={{ transform: "translate(-50%, -100%)", marginTop: "-8px" }}
      ></div>

      {/* Marker popup */}
      {activeMarker && (
        <div
          className="absolute bg-white p-3 rounded-lg shadow-lg border border-gray-200 z-50"
          style={{
            left: `${activeMarker.x}px`,
            top: `${activeMarker.y - 10}px`,
            transform: "translate(-50%, -100%)",
            zIndex: 100,
            minWidth: "160px",
          }}
        >
          <div>{activeMarker.content}</div>
        </div>
      )}
    </div>
  );
}

export function InteractiveMapTooltip({
  image,
  title,
  subtitle,
}: {
  image: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="flex flex-col items-center">
      <img src={image} alt={"image"} width={1024} height={724} />
      <h1 className={"mt-4"}>{title}</h1>
      {subtitle && (
        <p className={"mt-2 uppercase font-bold text-nowrap"}>{subtitle}</p>
      )}
    </div>
  );
}

export const ciInteractiveMapMarkers = (navigate: NavigateFunction) => [

  {
    id: "kong",
    x: 220,
    y: 80,
    content: (
      <InteractiveMapTooltip
        image={kongImage}
        title={"Kong"}
        subtitle={"LE NORD : UN LIVRE D'HISTOIRE A CIEL OUVERT"}
      />
    ),
    onClick: () => {
        navigate("/decouvertes/nos-regions/nord")

    },
  },
  {
    id: "Yamoussoukro",
    x: 185,
    y: 205,
    content: (
      <InteractiveMapTooltip
        image={basiliqueImage}
        title={"Yamoussoukro"}
        subtitle={"LE CENTRE : L'ART DE VIVRE EN COULEUR"}
      />
    ),
    onClick: () => {
        navigate("/decouvertes/nos-regions/centre")
    },
  },
  {
    id: "Tai",
    x: 45,
    y: 230,
    content: (
      <InteractiveMapTooltip
        image={taiImage}
        title={"Tai"}
        subtitle={"L'OUEST : SEULE LA NATURE REIGNE"}
      />
    ),
    onClick: () => {
        navigate("/decouvertes/nos-regions/ouest")
    },
},
    {
    id: "Grand-Bereby",
    x: 70,
    y: 320,
    content: (
      <InteractiveMapTooltip
        image={berebyImage}
        title={"Grand-Bereby"}
        subtitle={"LE SUD-OUEST : LA PERLE DE LA COTIERE"}
        />
    ),
    onClick: () => {
        navigate("/decouvertes/nos-regions/sud-ouest")

    },
  },
   {
    id: "Grand-Bassam",
    x: 240,
    y: 290,
    content: (
      <InteractiveMapTooltip
        image={bassamImage}
        title={"Grand-Bassam"}
        subtitle={"LE SUD : GRAND-BASSAM L'INITIATIVE FAITE FEMME"}
        />
    ),
    onClick: () => {
        navigate("/decouvertes/nos-regions/sud")
    },
  },
];
